import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Routes that don't belong to a single domain file. Notable grouping reasons:
 * - Admin routes use a custom named outlet (admin-content) for their child tabs
 * - Insurance is a parent route whose children are spread across this file
 * - Public routes (insurance email flows) are exported separately as MISC_PUBLIC_ROUTES
 */
export const MISC_ROUTES: Routes = [
    {
        path: 'admin',
        loadComponent: () => import('src/components/admin/admin.component').then(m => m.AdminComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'billingV2'
            },
            {
                path: 'billingV2',
                loadComponent: () => import('src/components/billing/billing-v2.component').then(m => m.BillingV2Component),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'adminBillingV2',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'settings',
                loadComponent: () => import('src/components/settings/settings.component').then(m => m.SettingsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'adminSettings',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'users',
                loadComponent: () => import('src/components/users/users.component').then(m => m.UsersComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'adminUsers',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'selectWorkflowConfiguration',
                loadComponent: () => import('src/components/workflow/configuration/select-workflow-configuration/select-workflow-configuration.component').then(m => m.SelectWorkflowConfigurationComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'adminSelectWorkflowConfiguration',
                    requiresPermission: true
                } satisfies RouteData
            }
        ]
    },

    // Admin routes that override the default layout (use full viewport outlets)
    {
        path: 'admin/manageObaTypes',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'adminManageObaTypes',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/workflow/configuration/manage-oba-types/manage-oba-types.component').then(m => m.ManageObaTypesComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/configuration/edit-workflow-side-rail/edit-workflow-side-rail.component').then(m => m.EditWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'shareDocuments',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'shareDocuments',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/manage-documents/share-document-workflow/share-document-workflow.component').then(m => m.ShareDocumentWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'multipleProducerShareDocuments',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/multiple-document-share/multiple-document-share-workflow.component').then(m => m.MultipleDocumentShareWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'producerShareDocument',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'producerShareDocument',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-share-document/producer-share-document-workflow.component').then(m => m.ProducerShareDocumentWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'documentSharedNotificationRedirect',
        loadComponent: () => import('src/components/document-shared-notification-redirect/document-shared-notification-redirect.component').then(m => m.DocumentSharedNotificationRedirectComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'viewDocument',
        loadComponent: () => import('src/components/view-document/view-document.component').then(m => m.ViewDocumentComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'viewDocument',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/view-document/view-document.component').then(m => m.ViewDocumentComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // Note: paymentProfile is an abstract parent in UI-Router
    {
        path: 'paymentProfile/createPaymentMethod',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'paymentProfileCreatePlatformPaymentMethod',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/payment-profile/create-platform-payment-method/create-platform-payment-method.component').then(m => m.CreatePlatformPaymentMethodComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'maintainResponses',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/maintain-questionnaire-responses-workflow/maintain-questionnaire-responses-workflow.component').then(m => m.MaintainQuestionnaireResponsesWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // Note: insurance is an abstract parent route in UI-Router
    {
        path: 'insurance',
        loadComponent: () => import('src/components/insurance/insurance.component').then(m => m.InsuranceComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    // Note: activatePendingInsuranceEmail and retainInsuranceAccount (unauthenticated)
    // have been moved to MISC_PUBLIC_ROUTES and are imported at root level in app.routes.ts

    // Contact changes workflow - multi-outlet container pattern
    {
        path: 'contact-changes',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/contact-change/workflow/contact-change-workflow.component').then(m => m.ContactChangeWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/contact-change/workflow/contact-change-side-rail/contact-change-side-rail.component').then(m => m.ContactChangeSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/components/footer/footer.component').then(m => m.FooterComponent),
                outlet: 'footer'
            }
        ]
    },

    // License print workflow - multi-outlet container pattern
    {
        path: 'license-print',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/license-print/workflow/license-print-workflow.component').then(m => m.LicensePrintWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/license-print/workflow/siderail/license-print-side-rail.component').then(m => m.LicensePrintSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Beneficiary redirect - multi-outlet container pattern
    {
        path: 'beneficiaryRedirect',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-beneficiaries/beneficiary-redirect/beneficiary-redirect.component').then(m => m.BeneficiaryRedirectComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    // Bulk renewals workflow - multi-outlet container pattern
    {
        path: 'bulk-renewals',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/bulk-renewals/bulk-renewals-workflow.component').then(m => m.BulkRenewalsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/bulk-renewals/siderail/bulk-renewals-side-rail.component').then(m => m.BulkRenewalsSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Manage Supervisor & Trainee Relationship (regulatory associations) workflow - multi-outlet container.
    // Route was missing after migration; "Manage Supervisor & Trainee Relationship" navigated to /error.
    {
        path: 'manage-associations',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/associations/workflow/associations-workflow.component').then(m => m.AssociationsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/associations/workflow/siderail/associations-side-rail.component').then(m => m.AssociationsSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Maintain background questions (attestation questionnaire) - multi-outlet container pattern
    {
        path: 'maintain-background-questions',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/personal-summary/maintain-background-questions/maintain-background-questions.component').then(m => m.MaintainBackgroundQuestionsComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/personal-summary/maintain-background-questions/side-rail/side-rail.component').then(m => m.MaintainSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'framedTransactionTab',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'framedTransactionTab',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/framed-transaction/framed-transaction-tab.component').then(m => m.FramedTransactionTabComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    }
];

/**
 * Public (Unauthenticated) Miscellaneous Routes
 *
 * These routes must be at root level in app.routes.ts, not nested in protected parent.
 * Includes insurance email activation and account retention flows that don't require authentication.
 */
export const MISC_PUBLIC_ROUTES: Routes = [
    // Activate pending insurance email
    {
        path: 'activatePendingInsuranceEmail',
        loadComponent: () => import('src/components/activate-pending-insurance-email/activate-pending-insurance-email.component').then(m => m.ActivatePendingInsuranceEmailComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'activatePendingInsuranceEmail',
            unauthenticated: true
        } satisfies RouteData
    },

    // Retain insurance account
    {
        path: 'retainInsuranceAccount',
        loadComponent: () => import('src/components/retain-insurance-account/retain-insurance-account.component').then(m => m.RetainInsuranceAccountComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'retainInsuranceAccount',
            unauthenticated: true
        } satisfies RouteData
    },

    // Docusign redirect page (return URL from DocuSign after signing)
    {
        path: 'docusignLandingPage',
        loadComponent: () => import('src/components/contract-documents/docusign-redirect-page/docusign-redirect-page.component').then(m => m.DocusignRedirectPageComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'docusignLandingPage',
            requiresPermission: false,
            unauthenticated: true
        } satisfies RouteData
    },

    // Docusign landing page (onboarding for new users after signing)
    {
        path: 'docusignLanding',
        loadComponent: () => import('src/app/header-content-container.component').then(m => m.HeaderContentContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'docusignLanding',
            requiresPermission: false,
            unauthenticated: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/docusign-landing/docusign-landing.component').then(m => m.DocusignLandingComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/header/public-header-black-logo.component').then(m => m.PublicHeaderBlackLogoComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    }
];

